import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "width_wrap" }
const _hoisted_3 = { class: "body_content" }
const _hoisted_4 = { class: "registration-form-content" }
const _hoisted_5 = { style: {"text-align":"right","margin-top":"20px"} }

import { Organization, OrganizationType } from "@/model/organization";
import { ref } from "vue";
import { getCallable } from "@/config/env";
import {
  NButton,
  NForm,
  NFormItem,
  NInput,
  NInputGroup,
  NSelect,
  FormInst,
  FormRules,
  useMessage,
  useNotification,
} from "naive-ui";
import { useRouter } from "vue-router";
import * as common from "@/assets/common/common";
import { USER_ROUTES } from "@/assets/route_constants";
import Banner from "@/components/Banner.vue"; // Added Banner import
import Loading from "@/components/Loading.vue"; // Added Loading import


export default /*@__PURE__*/_defineComponent({
  __name: 'RequestOrgRegist',
  setup(__props) {

const loading = ref(false); // Added loading state

const message = useMessage();
const notification = useNotification();
const router = useRouter();

const createOrganizationRequestCallable = getCallable(
  "createOrganizationRequest"
);
const checkOrgIdCallable = getCallable("checkOrgId");

const isCheckingId = ref<boolean>(false);
const registrationFormRef = ref<FormInst | null>(null);
const registrationModel = ref<Partial<Organization>>({
  uid: "",
  name: "",
  president: "",
  address: {
    // postCode, address, addressDetail will be filled by Daum Postcode API
    postCode: "",
    address: "",
    addressDetail: "",
  },
  dept: "",
  deptPersonName: "",
  deptPersonPhoneNumber: "",
  email: "",
  phoneNumber: "",
  description: "",
  type: null,
});
const idCheckMessage = ref<string>("");
const isIdAvailable = ref<boolean | null>(null);

const typeOptions = ref([
  {
    label: "초등학교",
    value: OrganizationType.ElementarySchool,
  },
  { label: "중학교", value: OrganizationType.MiddleSchool },
  { label: "고등학교", value: OrganizationType.HighSchool },
  { label: "대학교", value: OrganizationType.University },
  { label: "기타 기관(법인/재단 등)", value: OrganizationType.Etc },
]);

const registrationRules: FormRules = {
  uid: [
    {
      required: true,
      message: "기관 ID를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  name: [
    {
      required: true,
      message: "기관명을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  president: [
    {
      required: true,
      message: "대표자 이름을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  dept: [
    {
      required: true,
      message: "담당부서를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  deptPersonName: [
    {
      required: true,
      message: "담당자 이름을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  deptPersonPhoneNumber: [
    {
      required: true,
      message: "담당자 연락처를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  email: [
    {
      required: true,
      message: "이메일을 입력해주세요.",
      trigger: ["input", "blur"],
    },
    {
      type: "email",
      message: "올바른 이메일 형식이 아닙니다.",
      trigger: ["blur"],
    },
  ],
  phoneNumber: [
    {
      required: true,
      message: "전화번호를 입력해주세요.",
      trigger: ["input", "blur"],
    },
    {
      pattern: /^\d{2,3}-\d{3,4}-\d{4}$/,
      message:
        "전화번호 형식이 올바르지 않습니다. (예: 02-123-4567 또는 010-1234-5678)",
      trigger: ["blur"],
    },
  ],
  type: [
    { required: true, message: "기관 유형을 선택해주세요.", trigger: "change" },
  ],
};

const openPostcodeSearch = () => {
  if (
    typeof (window as any).daum === "undefined" ||
    typeof (window as any).daum.Postcode === "undefined"
  ) {
    message.error(
      "Daum 우편번호 서비스가 로드되지 않았습니다. 페이지를 새로고침하거나 관리자에게 문의하세요."
    );
    return;
  }
  new (window as any).daum.Postcode({
    oncomplete: (data: any) => {
      let addr = ""; // 주소 변수
      let extraAddr = ""; // 참고항목 변수

      if (data.userSelectedType === "R") {
        addr = data.roadAddress;
      } else {
        addr = data.jibunAddress;
      }

      if (data.userSelectedType === "R") {
        if (data.bname !== "" && /[동|로|가]$/g.test(data.bname)) {
          extraAddr += data.bname;
        }
        if (data.buildingName !== "" && data.apartment === "Y") {
          extraAddr +=
            extraAddr !== "" ? ", " + data.buildingName : data.buildingName;
        }
        if (extraAddr !== "") {
          extraAddr = " (" + extraAddr + ")";
        }
      }

      if (registrationModel.value.address) {
        registrationModel.value.address.postCode = data.zonecode;
        registrationModel.value.address.address = addr + extraAddr;
      }
      document.getElementById("addressDetailInput")?.focus();
    },
  }).open();
};

async function checkOrganizationId() {
  if (!registrationModel.value.uid) {
    idCheckMessage.value = "기관 ID를 먼저 입력해주세요.";
    isIdAvailable.value = false;
    return;
  }
  isCheckingId.value = true; // ID 중복 체크 로딩 시작
  idCheckMessage.value = "";
  isIdAvailable.value = null;
  try {
    const result: any = await checkOrgIdCallable({
      id: registrationModel.value.uid,
    });
    if (common.evalFunctionResult(result)) {
      const checkResult = common.getItemFromFunctionResult(result);
      if (checkResult) {
        idCheckMessage.value = "이미 사용 중인 ID입니다.";
        isIdAvailable.value = false;
      } else {
        idCheckMessage.value = "사용 가능한 ID입니다.";
        isIdAvailable.value = true;
      }
    } else {
      idCheckMessage.value = "ID 중복 체크 중 오류가 발생했습니다.";
      isIdAvailable.value = false;
    }
  } catch (error: any) {
    console.error("ID 중복 체크 실패:", error);
    idCheckMessage.value = "ID 중복 체크 중 오류가 발생했습니다.";
    isIdAvailable.value = false;
  } finally {
    isCheckingId.value = false; // ID 중복 체크 로딩 종료
  }
}

async function handleRegistrationRequestSubmit() {
  try {
    await registrationFormRef.value?.validate();
  } catch (errors) {
    message.error("입력 내용을 확인해주세요.");
    return;
  }
  if (isIdAvailable.value !== true) {
    message.error("기관 ID 중복 체크를 통과해야 합니다.");
    return;
  }
  loading.value = true;
  try {
    const result = await createOrganizationRequestCallable(
      registrationModel.value
    );
    if (common.evalFunctionResult(result)) {
      message.success("기관 등록요청 성공! 검토 후 연락드리겠습니다.");
      router.push(USER_ROUTES.Home.Path); // Navigate to home or a success page
    } else {
      notification.error({
        title: "등록요청 에러",
        content: `기관 등록요청 실패: ${
          common.getResultError(result) || "오류가 발생했습니다."
        }`,
        duration: 1500,
        closable: true,
      });
    }
  } catch (error) {
    notification.error({
      title: "에러",
      content: `기관 등록요청 실패: ${error || "오류가 발생했습니다."}`,
      duration: 3000,
      closable: true,
    });
  } finally {
    loading.value = false;
  }
}

function cancelRequest() {
  router.back(); // Or clear form: registrationModel.value = { ...initialState };
}

return (_ctx: any,_cache: any) => {
  const _component_n_form_item_gi = _resolveComponent("n-form-item-gi")!
  const _component_n_grid = _resolveComponent("n-grid")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives(_createVNode(Loading, null, null, 512), [
      [_vShow, loading.value]
    ]),
    _createVNode(Banner, {
      title: "",
      subtitle: "",
      description: "학생들과 함께 그들의 미래를 찾는 일을 하고자 합니다. <br>\n        자신의 미래에 관심이 있는 학생 누구에게나 도움을 주고자 합니다."
    }),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[18] || (_cache[18] = _createElementVNode("h1", { class: "sub_subject" }, [
          _createTextVNode("[기관] 신규기관등록 신청"),
          _createElementVNode("span")
        ], -1)),
        _createVNode(_unref(NForm), {
          ref_key: "registrationFormRef",
          ref: registrationFormRef,
          model: registrationModel.value,
          rules: registrationRules,
          "label-placement": "left",
          "label-width": "auto",
          "require-mark-placement": "right-hanging"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_n_grid, {
              cols: 3,
              "x-gap": 20
            }, {
              default: _withCtx(() => [
                _createVNode(_component_n_form_item_gi, {
                  label: "기관 ID",
                  path: "uid",
                  span: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInputGroup), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(NInput), {
                          value: registrationModel.value.uid,
                          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((registrationModel.value.uid) = $event)),
                          placeholder: "영문, 숫자 조합 (예: coko)",
                          style: {"width":"300px"}
                        }, null, 8, ["value"]),
                        _createVNode(_unref(NButton), {
                          type: "primary",
                          ghost: "",
                          onClick: checkOrganizationId,
                          loading: isCheckingId.value
                        }, {
                          default: _withCtx(() => _cache[13] || (_cache[13] = [
                            _createTextVNode("중복 체크")
                          ])),
                          _: 1
                        }, 8, ["loading"])
                      ]),
                      _: 1
                    }),
                    (idCheckMessage.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: _normalizeClass(isIdAvailable.value ? 'success-message' : 'error-message'),
                          style: {"width":"100%"}
                        }, _toDisplayString(idCheckMessage.value), 3))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "기관명",
                  path: "name"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.name,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((registrationModel.value.name) = $event)),
                      placeholder: "기관 전체 이름"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "대표자 이름",
                  path: "president"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.president,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((registrationModel.value.president) = $event)),
                      placeholder: "대표자 이름"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "기관 유형",
                  path: "type"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NSelect), {
                      value: registrationModel.value.type,
                      "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((registrationModel.value.type) = $event)),
                      placeholder: "기관 유형 선택",
                      options: typeOptions.value
                    }, null, 8, ["value", "options"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "우편번호",
                  path: "address.postCode"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInputGroup), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(NInput), {
                          value: registrationModel.value.address.postCode,
                          "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((registrationModel.value.address.postCode) = $event)),
                          placeholder: "우편번호",
                          disabled: true,
                          class: "force-black-text-disabled"
                        }, null, 8, ["value"]),
                        _createVNode(_unref(NButton), {
                          type: "primary",
                          ghost: "",
                          onClick: openPostcodeSearch,
                          style: {"margin-left":"5px"}
                        }, {
                          default: _withCtx(() => _cache[14] || (_cache[14] = [
                            _createTextVNode(" 주소검색 ")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "주소",
                  path: "address.address",
                  span: "2"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.address.address,
                      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((registrationModel.value.address.address) = $event)),
                      placeholder: "주소",
                      disabled: true,
                      class: "force-black-text-disabled"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "상세주소",
                  path: "address.addressDetail",
                  span: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      id: "addressDetailInput",
                      value: registrationModel.value.address.addressDetail,
                      "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((registrationModel.value.address.addressDetail) = $event)),
                      placeholder: "상세주소"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "담당부서",
                  path: "dept"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.dept,
                      "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((registrationModel.value.dept) = $event)),
                      placeholder: "담당부서 이름"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "전화번호",
                  path: "phoneNumber"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.phoneNumber,
                      "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => ((registrationModel.value.phoneNumber) = $event)),
                      placeholder: "대표 전화번호 (예: 02-1234-5678)"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, null, {
                  default: _withCtx(() => _cache[15] || (_cache[15] = [
                    _createTextVNode("   ")
                  ])),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "담당자 이름",
                  path: "deptPersonName"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.deptPersonName,
                      "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((registrationModel.value.deptPersonName) = $event)),
                      placeholder: "담당자 이름"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "담당자 연락처",
                  path: "deptPersonName"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.deptPersonPhoneNumber,
                      "onUpdate:value": _cache[10] || (_cache[10] = ($event: any) => ((registrationModel.value.deptPersonPhoneNumber) = $event)),
                      placeholder: "담당자 전화번호 (예: 010-1234-5678)"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "담당자 이메일",
                  path: "email"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.email,
                      "onUpdate:value": _cache[11] || (_cache[11] = ($event: any) => ((registrationModel.value.email) = $event)),
                      placeholder: "대표 이메일 주소"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_n_form_item_gi, {
                  label: "설명",
                  path: "description",
                  span: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NInput), {
                      value: registrationModel.value.description,
                      "onUpdate:value": _cache[12] || (_cache[12] = ($event: any) => ((registrationModel.value.description) = $event)),
                      type: "textarea",
                      placeholder: "기관에 대한 간략한 설명 (선택 사항)"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(NButton), {
              onClick: cancelRequest,
              type: "tertiary",
              style: {"margin-right":"10px"}
            }, {
              default: _withCtx(() => _cache[16] || (_cache[16] = [
                _createTextVNode("취소")
              ])),
              _: 1
            }),
            _createVNode(_unref(NButton), {
              type: "primary",
              onClick: handleRegistrationRequestSubmit
            }, {
              default: _withCtx(() => _cache[17] || (_cache[17] = [
                _createTextVNode("등록 요청")
              ])),
              _: 1
            })
          ])
        ])
      ])
    ])
  ]))
}
}

})